import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'

import {
  MODAL_CLOSE_DELAY_MS,
  ModalChildren,
  ContentWrapper,
  ModalComponent,
  ModalState,
} from 'components/styled/modal/modal-component'
import { AlertProps, useAlertContext } from 'components/styled/alert/context'
import styled from 'styled-components'
import { AlertIcon } from 'components/styled/icon/alert/alert-icon'
import { Small } from 'components/styled/typography'
import { getAlertMessageColor } from 'components/styled/alert-text'
import { Flex } from 'components/styled/containers/flex'

const DEFAULT_CLOSE_TIME_MS = 60000 // = 60sec

const AlertModal = styled(ModalComponent)`
  ${ModalChildren} {
    padding-top: ${({ theme }) => theme.space.s20};
    padding-bottom: ${({ theme }) => theme.space.s20};
    padding-left: ${({ theme }) => theme.space.s20};
  }

  ${({ theme }) => theme.mediaQuery.phone} {
    ${ModalChildren} {
      margin-top: 0;
    }

    ${ContentWrapper} {
      margin: ${({ theme }) => theme.space.s32} 0;
    }
  }
`

const StyledAlert = styled.div`
  display: flex;
  align-items: center;
`

const Title = styled(Small).attrs({
  fontWeight: 600,
  mb: 's0',
})``

const Text = styled(Small).attrs({
  color: 'grey90',
})``

export const Alert: React.FC<AlertProps> = ({
  variant,
  id,
  size = 'small',
  showCloseButton,
  title,
  text,
  closeLabel,
  closeAfterTimeout = false,
  closeTimeoutMs = DEFAULT_CLOSE_TIME_MS,
}) => {
  const [alertState, setAlertState] = useState<ModalState>(ModalState.Open)
  const { onCloseAlert } = useAlertContext()
  const timeoutRef = useRef<number | null>(null)

  // Changes alert state to opening,
  // so that the opening and closing animation gets initiated
  useLayoutEffect(() => {
    setAlertState(ModalState.Opening)
  }, [])

  useEffect(() => {
    if (!timeoutRef.current && closeAfterTimeout) {
      timeoutRef.current = window.setTimeout(() => {
        setAlertState(ModalState.Closing)
      }, closeTimeoutMs)
    }

    // Clear the timeout, if component unmounts (user closes the alert) before the alert is automatically closed
    return () => {
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current)
    }
  }, [])

  // After alertState has changed to opening or closing,
  // we must change the state from opening -> open or closing -> closed
  // so that the animation works
  useEffect(() => {
    if (alertState === ModalState.Opening) {
      setAlertState(ModalState.Open)
    } else if (alertState === ModalState.Closing) {
      setTimeout(() => {
        setAlertState(ModalState.Closed)
      }, MODAL_CLOSE_DELAY_MS)
    } else if (alertState === ModalState.Closed) {
      onCloseAlert(id)
    }
  }, [alertState])

  const isModalComponentVisible = [
    ModalState.Opening,
    ModalState.Open,
    ModalState.Closing,
  ].includes(alertState)

  const onClose = () => {
    setAlertState(oldVal => {
      if (oldVal === ModalState.Closed) return oldVal
      return ModalState.Closing
    })
  }

  return isModalComponentVisible ? (
    <AlertModal
      state={alertState}
      id={id}
      isOpen
      onClose={onClose}
      size={size}
      showCloseButton={showCloseButton}
      hasOverlay={false}
      hasStickyActionBar={false}
      closeLabel={closeLabel}
      isStackable
    >
      <StyledAlert>
        <AlertIcon variant={variant} />
        <Flex flexDirection="column" gap="s8">
          <Title color={getAlertMessageColor(variant)}>{title}</Title>
          {text && <Text>{text}</Text>}
        </Flex>
      </StyledAlert>
    </AlertModal>
  ) : null
}
